import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
  SportsEsports as GamingIcon,
  CloudQueue as CloudIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Router as RouterIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';

// ────────────────────────────────────────────────────────────
// 포트 포워딩 규칙 편집 서브컴포넌트
// ────────────────────────────────────────────────────────────
function PortForwardingRulesEditor({ rules, onChange }) {
  const emptyRule = { protocol: 'tcp', vm_port: '', description: '' };
  const [draftRule, setDraftRule] = useState(emptyRule);

  const handleAddRule = () => {
    const port = parseInt(draftRule.vm_port);
    if (!port || port < 1 || port > 65535) return;
    onChange([...rules, { ...draftRule, vm_port: port }]);
    setDraftRule(emptyRule);
  };

  const handleDeleteRule = (idx) => {
    onChange(rules.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      {rules.length > 0 && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>프로토콜</TableCell>
              <TableCell>VM 포트</TableCell>
              <TableCell>설명</TableCell>
              <TableCell width={40} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <Chip label={rule.protocol.toUpperCase()} size="small" color={rule.protocol === 'tcp' ? 'primary' : 'secondary'} />
                </TableCell>
                <TableCell>{rule.vm_port}</TableCell>
                <TableCell>{rule.description || '-'}</TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={() => handleDeleteRule(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* 새 규칙 추가 행 */}
      <Box display="flex" gap={1} alignItems="center">
        <TextField
          select
          size="small"
          label="프로토콜"
          value={draftRule.protocol}
          onChange={(e) => setDraftRule({ ...draftRule, protocol: e.target.value })}
          sx={{ width: 100 }}
        >
          <MenuItem value="tcp">TCP</MenuItem>
          <MenuItem value="udp">UDP</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="VM 포트"
          type="number"
          value={draftRule.vm_port}
          onChange={(e) => setDraftRule({ ...draftRule, vm_port: e.target.value })}
          inputProps={{ min: 1, max: 65535 }}
          sx={{ width: 110 }}
        />
        <TextField
          size="small"
          label="설명 (선택)"
          value={draftRule.description}
          onChange={(e) => setDraftRule({ ...draftRule, description: e.target.value })}
          sx={{ flexGrow: 1 }}
          placeholder="예: SSH, HTTP"
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddRule}
          disabled={!draftRule.vm_port}
        >
          추가
        </Button>
      </Box>
    </Box>
  );
}

// ────────────────────────────────────────────────────────────
// 프리셋 폼 공통 컴포넌트
// ────────────────────────────────────────────────────────────
function PresetForm({ form, onChange, availableGPUs, networks }) {
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    onChange({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="프리셋 이름" name="name" value={form.name} onChange={handleChange} required />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="설명" name="description" value={form.description} onChange={handleChange} />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="h6" gutterBottom>하드웨어 설정</Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth type="number" label="메모리 (MB)" name="memory"
          value={form.memory} onChange={handleChange}
          inputProps={{ min: 1024, step: 1024 }} required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth type="number" label="vCPU 수" name="vcpus"
          value={form.vcpus} onChange={handleChange}
          inputProps={{ min: 1 }} required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth type="number" label="디스크 크기 (GB)" name="disk_size"
          value={form.disk_size} onChange={handleChange}
          inputProps={{ min: 10 }} required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="GPU 할당" name="gpu_id" value={form.gpu_id} onChange={handleChange}>
          <MenuItem value="">할당 안함</MenuItem>
          {availableGPUs.map((gpu) => (
            <MenuItem key={gpu.pci_id} value={gpu.pci_id}>
              {gpu.tag ? `${gpu.tag} - ` : ''}{gpu.name} ({gpu.pci_id})
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="네트워크 프로필" name="network_profile" value={form.network_profile} onChange={handleChange}>
          {networks.map((network) => (
            <MenuItem key={network.name} value={network.name}>
              {network.name} ({network.cidr})
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={<Checkbox checked={form.is_default} onChange={handleChange} name="is_default" />}
          label="기본 프리셋으로 설정"
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <RouterIcon fontSize="small" color="primary" />
          <Typography variant="h6">포트 포워딩 규칙 (VM 생성 시 자동 적용)</Typography>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }} icon={false}>
          VM 생성 후 자동으로 포트 포워딩 규칙이 적용됩니다. 호스트 포트는 설정된 범위에서 자동 할당됩니다.
        </Alert>
        <PortForwardingRulesEditor
          rules={form.port_forwarding_rules || []}
          onChange={(rules) => onChange({ ...form, port_forwarding_rules: rules })}
        />
      </Grid>
    </Grid>
  );
}

// ────────────────────────────────────────────────────────────
// 메인 페이지
// ────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: '',
  description: '',
  memory: 4096,
  vcpus: 2,
  disk_size: 20,
  gpu_id: '',
  network_profile: 'default',
  is_default: false,
  port_forwarding_rules: [],
};

const HardwarePresetsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [availableGPUs, setAvailableGPUs] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fileInputRef, setFileInputRef] = useState(null);
  const [presetForm, setPresetForm] = useState(EMPTY_FORM);

  const loadData = async () => {
    setLoading(true);
    try {
      const [presetsRes, gpusRes, networksRes] = await Promise.all([
        apiService.getHardwarePresets(),
        apiService.getAvailableGPUsWithTags(),
        apiService.getNetworks(),
      ]);
      setPresets(presetsRes.data.presets || []);
      setAvailableGPUs(gpusRes.data.gpus || []);
      setNetworks(networksRes.data.networks || []);
    } catch {
      enqueueSnackbar('데이터를 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => setPresetForm(EMPTY_FORM);

  const handleCreatePreset = async () => {
    if (!presetForm.name.trim()) {
      enqueueSnackbar('프리셋 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await apiService.createHardwarePreset({ ...presetForm, gpu_id: presetForm.gpu_id || null });
      enqueueSnackbar('VM 프리셋이 생성되었습니다.', { variant: 'success' });
      setCreateDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      enqueueSnackbar(`프리셋 생성 실패: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePreset = async () => {
    if (!presetForm.name.trim()) {
      enqueueSnackbar('프리셋 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await apiService.updateHardwarePreset(selectedPreset.id, { ...presetForm, gpu_id: presetForm.gpu_id || null });
      enqueueSnackbar('VM 프리셋이 수정되었습니다.', { variant: 'success' });
      setEditDialogOpen(false);
      resetForm();
      setSelectedPreset(null);
      loadData();
    } catch (error) {
      enqueueSnackbar(`프리셋 수정 실패: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditDialog = (preset) => {
    setSelectedPreset(preset);
    setPresetForm({
      name: preset.name,
      description: preset.description || '',
      memory: preset.memory,
      vcpus: preset.vcpus,
      disk_size: preset.disk_size,
      gpu_id: preset.gpu_id || '',
      network_profile: preset.network_profile,
      is_default: preset.is_default,
      port_forwarding_rules: preset.port_forwarding_rules || [],
    });
    setEditDialogOpen(true);
  };

  const handleDeletePreset = async () => {
    if (!selectedPreset) return;
    setDeleting(true);
    try {
      await apiService.deleteHardwarePreset(selectedPreset.id);
      enqueueSnackbar(`프리셋 '${selectedPreset.name}'이 삭제되었습니다.`, { variant: 'success' });
      setDeleteDialogOpen(false);
      setSelectedPreset(null);
      loadData();
    } catch (error) {
      enqueueSnackbar(`프리셋 삭제 실패: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleExportPresets = () => {
    const exportData = {
      export_timestamp: new Date().toISOString(),
      presets: presets.map(({ name, description, memory, vcpus, disk_size, gpu_id, network_profile, is_default, port_forwarding_rules }) =>
        ({ name, description, memory, vcpus, disk_size, gpu_id, network_profile, is_default, port_forwarding_rules: port_forwarding_rules || [] })
      ),
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vm-presets-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    enqueueSnackbar('VM 프리셋이 JSON 파일로 내보내졌습니다.', { variant: 'success' });
  };

  const handleImportPresets = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        if (!importData.presets || !Array.isArray(importData.presets)) {
          throw new Error('올바른 프리셋 데이터 형식이 아닙니다.');
        }
        let successCount = 0;
        let failCount = 0;
        for (const presetData of importData.presets) {
          try {
            await apiService.createHardwarePreset({ ...presetData, gpu_id: presetData.gpu_id || null });
            successCount++;
          } catch {
            failCount++;
          }
        }
        if (successCount > 0) {
          enqueueSnackbar(`${successCount}개의 프리셋이 가져와졌습니다.`, { variant: 'success' });
          loadData();
        }
        if (failCount > 0) {
          enqueueSnackbar(`${failCount}개의 프리셋 가져오기에 실패했습니다.`, { variant: 'warning' });
        }
      } catch (error) {
        enqueueSnackbar(`파일 가져오기 실패: ${error.message}`, { variant: 'error' });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const getPresetIcon = (presetName) => {
    const name = presetName.toLowerCase();
    if (name.includes('게임') || name.includes('gpu')) return <GamingIcon />;
    if (name.includes('개발')) return <CodeIcon />;
    if (name.includes('서버')) return <CloudIcon />;
    if (name.includes('경량') || name.includes('테스트')) return <StorageIcon />;
    return <MemoryIcon />;
  };

  const formatMemory = (mb) => mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;

  const getGPUName = (gpuId) => {
    if (!gpuId) return 'None';
    const gpu = availableGPUs.find(g => g.pci_id === gpuId);
    if (!gpu) return gpuId;
    return gpu.tag ? `${gpu.tag} - ${gpu.name} (${gpu.pci_id})` : `${gpu.name} (${gpu.pci_id})`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">VM 프리셋 관리</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>새로고침</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportPresets} disabled={presets.length === 0}>
            JSON 내보내기
          </Button>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInputRef?.click()}>
            JSON 가져오기
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setCreateDialogOpen(true); }}>
            새 프리셋 생성
          </Button>
          <input
            type="file"
            ref={(ref) => setFileInputRef(ref)}
            onChange={handleImportPresets}
            accept=".json"
            style={{ display: 'none' }}
          />
        </Box>
      </Box>

      {/* 프리셋 카드 뷰 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {presets.map((preset) => {
          const pfRules = preset.port_forwarding_rules || [];
          return (
            <Grid item xs={12} sm={6} md={4} key={preset.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {getPresetIcon(preset.name)}
                    <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                      {preset.name}
                    </Typography>
                    {preset.is_default && <Chip label="기본" color="primary" size="small" />}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {preset.description || '설명 없음'}
                  </Typography>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" display="block">메모리: {formatMemory(preset.memory)}</Typography>
                    <Typography variant="caption" display="block">vCPU: {preset.vcpus}개</Typography>
                    <Typography variant="caption" display="block">디스크: {preset.disk_size} GB</Typography>
                    <Typography variant="caption" display="block">GPU: {getGPUName(preset.gpu_id)}</Typography>
                    <Typography variant="caption" display="block">네트워크: {preset.network_profile}</Typography>
                  </Box>

                  {pfRules.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                        <RouterIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          포트 포워딩 ({pfRules.length}개)
                        </Typography>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {pfRules.map((r, i) => (
                          <Chip
                            key={i}
                            label={`${r.protocol.toUpperCase()}:${r.vm_port}${r.description ? ` (${r.description})` : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Tooltip title="편집">
                      <IconButton onClick={() => handleOpenEditDialog(preset)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="삭제">
                      <IconButton onClick={() => { setSelectedPreset(preset); setDeleteDialogOpen(true); }} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {presets.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">등록된 VM 프리셋이 없습니다.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            새 프리셋을 생성하여 VM 생성 시 하드웨어 설정과 포트 포워딩 규칙을 빠르게 적용하세요.
          </Typography>
        </Paper>
      )}

      {/* 생성 다이얼로그 */}
      <Dialog open={createDialogOpen} onClose={() => { setCreateDialogOpen(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>새 VM 프리셋 생성</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <PresetForm
              form={presetForm}
              onChange={setPresetForm}
              availableGPUs={availableGPUs}
              networks={networks}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); resetForm(); }}>취소</Button>
          <Button onClick={handleCreatePreset} variant="contained" disabled={saving || !presetForm.name.trim()}>
            {saving ? '생성 중...' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 편집 다이얼로그 */}
      <Dialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); resetForm(); setSelectedPreset(null); }} maxWidth="md" fullWidth>
        <DialogTitle>VM 프리셋 편집</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <PresetForm
              form={presetForm}
              onChange={setPresetForm}
              availableGPUs={availableGPUs}
              networks={networks}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); resetForm(); setSelectedPreset(null); }}>취소</Button>
          <Button onClick={handleUpdatePreset} variant="contained" disabled={saving || !presetForm.name.trim()}>
            {saving ? '수정 중...' : '수정'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>프리셋 삭제 확인</DialogTitle>
        <DialogContent>
          {selectedPreset && (
            <Typography variant="body1">
              VM 프리셋 '{selectedPreset.name}'을 삭제하시겠습니까?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDeletePreset} color="error" variant="contained" disabled={deleting}>
            {deleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HardwarePresetsPage;
